import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { StyleSheet, ActivityIndicator, ScrollView } from 'react-native';
import {
  DNABox,
  DNAText,
  DNAButton,
  Toggle,
  GenericToast,
  ToastOrientations,
  useToast,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { PromptValues, UserRole } from '@alucio/aws-beacon-amplify/src/models';
import { usePromptValuesState } from 'src/state/context/PromptValues/PromptValuesStateProvider';
import {
  useComplianceResults,
  ComplianceRuleStatusData,
} from 'src/state/context/Compliance/ComplianceStateProvider';
import { useCurrentUserORM } from 'src/state/redux/selector/user';
import DNAEmpty, { EmptyVariant } from 'src/components/DNA/Empty/DNAEmpty';
import ComplianceRuleStatusList from './ComplianceRuleStatusList';
import AIDisclaimer from './AIDisclaimer';
import { useLangflowCompliance } from 'src/hooks/useLangflowCompliance';
import logger from 'src/utils/logger';

enum CheckerState {
  SELECTING = 'SELECTING',
  CHECKING = 'CHECKING',
  RESULTS = 'RESULTS',
}

interface ComplianceRuleCheckerProps {
  customDeckId: string;
  pageIds: string[];
  onCheckCompliance?: (selectedRules: PromptValues[]) => Promise<ComplianceRuleStatusData[]>;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: 24,
  },
  header: {
    gap: 12,
  },
  contentContainer: {
    paddingHorizontal: 16,
  },
  loadingContainer: {
    paddingVertical: 80,
  },
  loadingText: {
    color: colors['color-text-secondary'],
  },
  noPagesContainer: {
    paddingVertical: 40,
    paddingHorizontal: 20,
  },
  noPagesText: {
    color: colors['color-text-secondary'],
  },
  noPagesTitle: {
    color: colors['color-text-primary'],
    marginBottom: 8,
  },
  noEnabledRulesContainer: {
    paddingVertical: 20,
    paddingHorizontal: 24,
    backgroundColor: colors['color-gray-50'],
    borderRadius: 8,
    marginBottom: 16,
    // this is required for the disabled overlay to be visible
    width: '100%',
  },
  noEnabledRulesText: {
    color: colors['color-text-secondary'],
    // this is required because the text can have multiple lines
    textAlign: 'center',
  },
  toggleListContainer: {
    backgroundColor: colors['color-text-white'],
  },
  ruleItem: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors['color-gray-80'],
    marginBottom: 12,
  },
  ruleItemDisabled: {
    backgroundColor: colors['color-ui2-neutral-50'],
  },
  ruleText: {
    color: colors['color-text-primary'],
    lineHeight: 20,
  },
  ruleTextDisabled: {
    color: colors['color-text-secondary'],
    opacity: 0.6,
  },
  switchContainer: {
    alignSelf: 'flex-end',
  },
  switchContainerDisabled: {
    opacity: 0.5,
  },
  emptyStateContainer: {
    padding: 40,
  },
  complianceBox: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: colors['color-gray-80'],
  },
});

const useComplianceRuleChecker = ({
  customDeckId,
  pageIds,
  onCheckCompliance,
}: ComplianceRuleCheckerProps) => {
  const { selectedValues, values, handleToggleSelection, isSelected, loading } = usePromptValuesState();
  const { results, setResults } = useComplianceResults();
  const [checkerState, setCheckerState] = useState<CheckerState>(CheckerState.SELECTING);
  const [, setShowNoPagesMessage] = useState(false);
  const [hasRunCheck, setHasRunCheck] = useState(false);
  const lastErrorToastIdRef = useRef<string | null>(null);
  const lastErrorRef = useRef<string | null>(null);
  const toast = useToast();

  const currentUser = useCurrentUserORM();
  const isPublisher = currentUser?.model.role === UserRole.TENANT_PUBLISHER;

  const {
    checkCompliance,
    error: langflowError,
    reset: resetLangflow,
  } = useLangflowCompliance();

  const selectedRules = useMemo(
    () => values.filter(rule => selectedValues.has(rule.id)),
    [values, selectedValues],
  );

  const hasSelection = useMemo(
    () => selectedValues.size > 0,
    [selectedValues],
  );

  const hasEnabledRules = useMemo(
    () => results.length > 0 && results.some(rule => rule.enabled),
    [results],
  );

  useEffect(() => {
    return () => {
      setCheckerState(CheckerState.SELECTING);
      setResults([]);
      setHasRunCheck(false);
      setShowNoPagesMessage(false);
      resetLangflow();
      if (lastErrorToastIdRef.current) {
        toast.remove(lastErrorToastIdRef.current);
        lastErrorToastIdRef.current = null;
      }
      lastErrorRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (langflowError && langflowError !== lastErrorRef.current) {
      console.error('Langflow error:', langflowError);
      setCheckerState(CheckerState.SELECTING);

      if (lastErrorToastIdRef.current) {
        toast.remove(lastErrorToastIdRef.current);
      }

      const toastId = toast.add(
        <GenericToast
          title={langflowError}
          status="error"
          width={400}
        />,
        ToastOrientations.TOP_RIGHT,
        true,
        5000,
      );
      lastErrorToastIdRef.current = toastId;
      lastErrorRef.current = langflowError;
    }
  }, [langflowError, toast]);

  const handleCheckCompliance = useCallback(async () => {
    if (pageIds.length === 0) {
      setShowNoPagesMessage(true);

      toast.add(
        <GenericToast
          title="No pages available for validation"
          status="warning"
          description={'Please add PDF or PPTX documents\nto validate compliance'}
          width={320}
        />,
        ToastOrientations.TOP_RIGHT,
        true,
        5000,
      );
      return;
    }

    const currentResults = results;

    setShowNoPagesMessage(false);
    setResults([]);
    setCheckerState(CheckerState.CHECKING);
    setHasRunCheck(true);
    resetLangflow();

    if (lastErrorToastIdRef.current) {
      toast.remove(lastErrorToastIdRef.current);
      lastErrorToastIdRef.current = null;
    }
    lastErrorRef.current = null;

    try {
      let complianceResults: ComplianceRuleStatusData[];

      if (onCheckCompliance) {
        complianceResults = await onCheckCompliance(selectedRules);
        setResults(complianceResults);
        setCheckerState(CheckerState.RESULTS);
      } else {
        const rulesToSend = currentResults.length > 0
          ? values.filter(rule => {
            const result = currentResults.find(r => r.id === rule.id);
            return result ? result.enabled : false;
          })
          : selectedRules;

        if (rulesToSend.length === 0) {
          toast.add(
            <GenericToast
              title="No enabled rules"
              status="warning"
              description="Please enable at least one rule to run compliance check"
              width={400}
            />,
            ToastOrientations.TOP_RIGHT,
            true,
            5000,
          );
          setCheckerState(CheckerState.RESULTS);
          return;
        }

        await checkCompliance(
          {
            rules: rulesToSend.map(r => r.id),
            allRules: values.map(rule => ({ id: rule.id, text: rule.value || '' })),
            pages: pageIds,
            customDeckId,
          },
          (langflowResults, _allRules, _selectedRuleIds) => {
            const complianceResults: ComplianceRuleStatusData[] = langflowResults.map(result => ({
              id: result.ruleId,
              text: result.ruleText,
              status: result.status,
              explanation: result.explanation,
              enabled: result.wasSelected,
            }));

            setResults(complianceResults);
            setCheckerState(CheckerState.RESULTS);
          },
        );
      }
    } catch (error) {
      logger.compliance.error('Error checking compliance:', error);
      setCheckerState(CheckerState.SELECTING);
    }
  }, [
    pageIds,
    toast,
    onCheckCompliance,
    selectedRules,
    results,
    values,
    customDeckId,
    checkCompliance,
    setResults,
    resetLangflow,
  ]);

  const handleToggleResult = useCallback((ruleId: string, enabled: boolean) => {
    if (!isPublisher) {
      return;
    }

    setResults(prev =>
      prev.map(rule =>
        rule.id === ruleId ? { ...rule, enabled } : rule,
      ),
    );
  }, [isPublisher, setResults]);

  const handleChangeSelection = useCallback(() => {
    setCheckerState(CheckerState.SELECTING);
    setResults([]);
    resetLangflow();
  }, [setResults, resetLangflow]);

  return {
    checkerState,
    values,
    loading,
    selectedValues,
    results,
    hasSelection,
    hasEnabledRules,
    hasRunCheck,
    isPublisher,
    isSelected,
    handleToggleSelection,
    handleCheckCompliance,
    handleToggleResult,
    handleChangeSelection,
    pageIds,
  };
};

const ComplianceRuleChecker: React.FC<ComplianceRuleCheckerProps> = (props) => {
  const {
    checkerState,
    values,
    loading,
    results,
    hasSelection,
    hasEnabledRules,
    hasRunCheck,
    isPublisher,
    isSelected,
    handleToggleSelection,
    handleCheckCompliance,
    handleToggleResult,
    handleChangeSelection,
    pageIds,
  } = useComplianceRuleChecker(props);

  const ComplianceRuleToggleItem: React.FC<{
    rule: PromptValues;
  }> = ({ rule }) => {
    const selected = isSelected(rule.id);

    return (
      <DNABox
        appearance="col"
        style={[styles.ruleItem, !selected && styles.ruleItemDisabled]}
        testID={`compliance-rule-toggle-${rule.id}`}
      >
        <DNABox fill>
          <DNAText p2 style={[styles.ruleText, !selected && styles.ruleTextDisabled]}>
            {rule.value}
          </DNAText>
        </DNABox>
        {isPublisher && (
          <DNABox style={[styles.switchContainer, !selected && styles.switchContainerDisabled]}>
            <Toggle.Kitten
              checked={selected}
              onChange={() => handleToggleSelection(rule.id)}
              testID={`compliance-rule-switch-${rule.id}`}
              status="success"
              size="sm"
            />
          </DNABox>
        )}
      </DNABox>
    );
  };

  const renderToggleList = useCallback(() => {
    if (loading) {
      return (
        <DNABox fill alignX="center" alignY="center">
          <DNAText>Loading...</DNAText>
        </DNABox>
      );
    }

    if (values.length === 0) {
      return (
        <DNABox fill style={styles.emptyStateContainer}>
          <DNAEmpty
            emptyVariant={EmptyVariant.CustomLabelEmpty}
            customEmptyLabel={
              'No compliance rules found. All study results must be presented ' +
              'together including efficacy and safety'
            }
          />
        </DNABox>
      );
    }

    return (
      <DNABox style={styles.toggleListContainer} appearance="col" fill>
        <ScrollView
          showsVerticalScrollIndicator={true}
          bounces={false}
        >
          {values.map((rule) => (
            <ComplianceRuleToggleItem
              key={rule.id}
              rule={rule}
            />
          ))}
        </ScrollView>
      </DNABox>
    );
  }, [loading, values, isSelected, handleToggleSelection, isPublisher]);

  const renderContent = useCallback(() => {
    switch (checkerState) {
      case CheckerState.SELECTING:
        return (
          <>
            <DNABox style={styles.contentContainer} fill>
              {pageIds.length === 0 ? (
                <DNABox
                  fill
                  appearance="col"
                  alignX="center"
                  alignY="center"
                  spacing="md"
                  style={styles.noPagesContainer}
                >
                  <DNAText h4 bold style={styles.noPagesTitle}>
                    No valid pages found
                  </DNAText>
                  <DNAText h5 style={styles.noPagesText}>
                    Please add PDF or PPTX documents to your presentation to validate compliance rules.
                  </DNAText>
                </DNABox>
              ) : (
                renderToggleList()
              )}
            </DNABox>

            <DNABox appearance="col" style={styles.complianceBox} spacing="sm">
              <DNAButton
                appearance="filled"
                status="primary"
                size="lg"
                onPress={handleCheckCompliance}
                disabled={!hasSelection || pageIds.length === 0}
                testID="check-compliance-button"
                stretch
              >
                Check compliance
              </DNAButton>
              <AIDisclaimer />
            </DNABox>
          </>
        );

      case CheckerState.CHECKING:
        return (
          <DNABox
            fill
            appearance="col"
            alignX="center"
            alignY="center"
            spacing="md"
            style={styles.loadingContainer}
          >
            <ActivityIndicator size="large" color={colors['color-primary-500']} />
            <DNAText h5 style={styles.loadingText}>Checking compliance...</DNAText>
          </DNABox>
        );

      case CheckerState.RESULTS:
        return (
          <>
            <DNABox style={styles.contentContainer} fill>
              <ComplianceRuleStatusList
                rules={results}
                onToggleRule={isPublisher ? handleToggleResult : undefined}
                showDetails={hasRunCheck}
              />

              {!hasEnabledRules && isPublisher && (
                <DNABox
                  appearance="col"
                  alignX="center"
                  alignY="center"
                  spacing="sm"
                  style={styles.noEnabledRulesContainer}
                >
                  <DNAText p2 style={styles.noEnabledRulesText}>
                    No rules are currently enabled. You can enable rules using the toggles above, or click
                    &quot;Change Selection&quot; below to select different rules.
                  </DNAText>
                </DNABox>
              )}
            </DNABox>

            <DNABox appearance="col" alignX="stretch" spacing="md" style={styles.complianceBox}>
              {!hasEnabledRules && isPublisher ? (
                <DNABox appearance="col" alignX="stretch" spacing="sm">
                  <DNAButton
                    appearance="filled"
                    status="primary"
                    size="lg"
                    onPress={handleChangeSelection}
                    testID="back-to-selection-button"
                    stretch
                  >
                    Change Selection
                  </DNAButton>
                  <DNAButton
                    appearance="filled"
                    status="primary"
                    size="lg"
                    onPress={handleCheckCompliance}
                    disabled={true}
                    testID="check-compliance-again-button-disabled"
                    stretch
                  >
                    Check compliance
                  </DNAButton>
                </DNABox>
              ) : (
                <DNAButton
                  appearance="filled"
                  status="primary"
                  size="lg"
                  onPress={handleCheckCompliance}
                  testID="check-compliance-again-button"
                  stretch
                >
                  Check compliance
                </DNAButton>
              )}
              <AIDisclaimer />
            </DNABox>
          </>
        );

      default:
        return null;
    }
  }, [
    checkerState,
    pageIds,
    renderToggleList,
    handleCheckCompliance,
    hasSelection,
    results,
    isPublisher,
    handleToggleResult,
    hasRunCheck,
    hasEnabledRules,
    handleChangeSelection,
  ]);

  return (
    <DNABox style={styles.container} appearance="col">
      {renderContent()}
    </DNABox>
  );
};

export default ComplianceRuleChecker;
